import React from 'react';

import { Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

import { DecorativeLogo } from './BackgroundSVG';
import { AlineLogo } from '../icons/AlineLogo';
import { DemographicsIcon } from '../icons/DemographicsIcon';
import { DoneAllIcon } from '../icons/DoneAllIcon';
import { GroupIcon } from '../icons/GroupIcon';

const useStyles = makeStyles()(theme => ({
  activeOpacity: {
    opacity: 0.8,
  },
  bottomHalf: {
    bottom: 0,
    height: '50%',
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    width: '100%',
  },
  decorativeLogo: {
    bottom: 0,
    height: '100%',
    left: 0,
    objectFit: 'cover',
    position: 'absolute',
    width: '100%',
  },
  description: {
    color: theme.palette.common.white,
    fontSize: 12,
  },
  logo: {
    marginBottom: theme.spacing(4),
  },
  opacity: {
    opacity: 0.6,
  },
  sidebar: {
    background: '#1A2551',
    border: '1px solid #979797',
    borderRadius: theme.shape.borderRadius,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    width: 340,
  },
  stepIcon: {
    alignItems: 'center',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.common.white,
    display: 'flex',
    height: 40,
    justifyContent: 'center',
    minWidth: 40,
    width: 40,
  },
  stepItem: {
    alignItems: 'flex-start',
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0),
  },
  stepText: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    color: theme.palette.common.white,
    fontSize: 14,
    fontWeight: 600,
  },
  topHalf: {
    color: theme.palette.common.white,
    padding: theme.spacing(4),
  },
}));

interface SidePanelProps {
  currentStep: number;
}

export function SidePanel({ currentStep }: SidePanelProps) {
  const theme = useTheme();
  const { classes } = useStyles();
  const steps = [
    {
      description: 'Provide info about you and your org',
      icon: currentStep <= 0 ? <DemographicsIcon /> : <DoneAllIcon fill="#00954D" />,
      id: 'demographic',
      title: 'Demographic information',
    },
    {
      description: 'Start collaborating with your team',
      icon: currentStep <= 1 ? <GroupIcon /> : <DoneAllIcon fill="#00954D" />,
      id: 'team',
      title: 'Create your team',
    },
  ];

  return (
    <div className={classes.sidebar}>
      <div className={classes.topHalf}>
        <div className={classes.logo}>
          <AlineLogo variant="white" />
        </div>
        <Typography gutterBottom color="inherit" variant="h5">
          Welcome to Aline
        </Typography>
        <Typography gutterBottom className={classes.activeOpacity} color="inherit">
          {"Let's get started with a few things and set you up onto the Aline platform."}
        </Typography>
        {steps.map((step, index) => {
          const isAfterStep = currentStep > index;
          const isStepActive = index === currentStep;
          const isAtOrAfterStep = currentStep >= index;

          return (
            <div key={step.id} className={classes.stepItem}>
              <div
                className={classes.stepIcon}
                style={{
                  backgroundColor: isAtOrAfterStep ? theme.palette.common.white : undefined,
                  color: isAtOrAfterStep ? theme.palette.primary.main : undefined,
                }}
              >
                {step.icon}
              </div>
              <div className={classes.stepText}>
                <Typography className={clsx(classes.title, isAfterStep && classes.opacity)}>
                  {step.title}
                </Typography>
                <Typography
                  className={clsx(
                    classes.description,
                    isAfterStep && classes.opacity,
                    isStepActive && classes.activeOpacity
                  )}
                >
                  {step.description}
                </Typography>
              </div>
            </div>
          );
        })}
      </div>
      <div className={classes.bottomHalf}>
        <DecorativeLogo />
      </div>
    </div>
  );
}
